import { useMemo } from 'react';
import { t } from 'i18next';
import { FilterOption } from 'models/Filters';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useOperationStepValues from 'router/data/useOperationStepValues';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import { LOCATION_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/LocationFilter/LocationFilter.component';
import { INDUSTRY_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/IndustryFilter/IndustryFilter.component';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { TIMEFRAME } from 'dateUtils';
import {
  DEAL_FROM_FILTER_KEY,
  DEAL_TO_FILTER_KEY,
  EBIDTA_TO_FILTER_KEY,
  EBITDA_FROM_FILTER_KEY,
} from '../../../../../../components/PortfolioSellerFilters/data';
import { BuyerListingStats } from '../../../../models';
import { buyerFilterOptions, COUNT_VALUES_MAP } from '../../utils';

const DEAL_STATUS_FILTER_KEY = 'or';

const useBuyerDealsFilterOptions = (buyerListingStats: BuyerListingStats) => {
  const { getPriceRangeFilter } = useGetFilterOptions();

  const { locationOptions, industrySelectOptions } =
    useOperationStepValues(true);

  const createSelectOptions = useCreateSelectOptions();

  return useMemo(
    () =>
      [
        {
          value: DEAL_STATUS_FILTER_KEY,
          label: t('status'),
          shouldSortOptions: false,
          options: buyerFilterOptions.map(({ label, value }) => {
            const count = buyerListingStats?.[COUNT_VALUES_MAP[value]];

            return {
              label: `${t(label)}${count ? ` (${count})` : ''}`,
              value,
            };
          }),
        },
        getPriceRangeFilter(
          t('price'),
          DEAL_FROM_FILTER_KEY,
          DEAL_TO_FILTER_KEY,
        ),
        getPriceRangeFilter(
          t('ebitda'),
          EBITDA_FROM_FILTER_KEY,
          EBIDTA_TO_FILTER_KEY,
        ),
        {
          value: LOCATION_FILTER_KEY,
          label: t('location'),
          isMultiple: true,
          options: locationOptions,
        },
        {
          value: INDUSTRY_FILTER_KEY,
          label: t('industry'),
          isMultiple: true,
          isSearchable: true,
          options: industrySelectOptions,
        },
        {
          value: TIMEFRAME_PARAM_KEYWORD,
          label: t('createdDuring'),
          shouldSortOptions: false,
          options: createSelectOptions(Object.values(TIMEFRAME)),
        },
      ] as FilterOption[],
    [
      buyerListingStats,
      locationOptions,
      industrySelectOptions,
      createSelectOptions,
      getPriceRangeFilter,
    ],
  );
};

export default useBuyerDealsFilterOptions;
